import React, { useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';

import { getCurrency, getLanguage, getMarket } from '@travel/i18n';
import { isEmptyArray } from '@travel/utils';

import type { Props as ProviderSummaryProps } from 'components/ProviderSummary';
import ProviderSummary from 'components/ProviderSummary';
import Swiper from 'components/Swiper';

import { getLocation } from 'store/__router/selectors';
import { getIsGlobalCurrency } from 'store/markets/selectors';
import { getItems } from 'store/providerList/selectors';

import { EMPTY_ARRAY } from 'constants/defaultValue';
import { PROVIDER_CONTRACT_TYPES } from 'constants/provider';
import { Link } from 'core/universalRouter/Link';
import paths from 'core/universalRouter/paths';
import type { ProviderItem } from 'ProviderList-Types';

import styles from './providerSummarySlider.module.scss';

type Props = {
  /** String of selected provider id for initial state */
  selectedProviderId: string;
  /** Callback to be called when active slide is updated */
  onSlideChange?: (item: ProviderItem) => void;
};

const SLIDER_SPEED = 150;

/**
 * Note: This slider will render only 3 items which are the previous, main (selected) and next item
 */
function ProviderSummarySlider(props: Props) {
  const cache = useRef<{ [key: string]: ProviderSummaryProps }>({});

  const { providers = EMPTY_ARRAY } = useSelector(getItems);
  const location = useSelector(getLocation);
  const language = useSelector(getLanguage);
  const currency = useSelector(getCurrency);
  const market = useSelector(getMarket);
  const isGlobalCurrency = useSelector(getIsGlobalCurrency);

  const mainProviderIndex = providers?.findIndex(
    provider => props.selectedProviderId === provider?.id,
  );

  const onChange = (_currentIndex: number, nextIndex: number) => {
    // NOTE: Set time + 100 is to make sure that the component will be updated after slide was changed
    setTimeout(() => props.onSlideChange?.(providers?.[nextIndex]), SLIDER_SPEED);
  };

  // NOTE: to cache a computed props that will be reused when state is updated
  const getProviderProps = useCallback(
    (provider: ProviderItem) => {
      if (cache.current[provider?.id]) {
        return cache.current[provider?.id];
      }

      const propsObj = {
        id: provider.id,
        price: provider.price,
        name: provider.name,
        reviewScore: provider.reviews.score,
        totalReviews: provider.reviews.count,
        reviewLabel: provider.reviews.label,
        starRating: provider.starRating,
        language: language,
        currency: currency,
        isGlobalCurrency: isGlobalCurrency,
        market: market.marketCode,
        media: provider.media?.[0],
        isDirectContract: provider.contractType === PROVIDER_CONTRACT_TYPES.DIRECT,
      };

      cache.current[provider?.id] = propsObj;
      return propsObj;
    },
    [currency, isGlobalCurrency, language, market.marketCode],
  );

  const getProviderInfoLink = (pathId?: string) => {
    if (!pathId) {
      return '';
    }
    const baseUrl = paths.providerInfo.pathResolver(pathId);
    return baseUrl + location.search;
  };

  if (isEmptyArray(providers)) {
    return null;
  }

  return (
    <div data-testid="providerSummarySlider-wrapper" className={styles.wrapper}>
      <Swiper
        onChange={onChange}
        initialSlide={mainProviderIndex}
        paginationType="arrows"
        shouldRenderAllChild={false}
      >
        {providers?.map((provider: ProviderItem) => (
          <Link
            to={getProviderInfoLink(provider.pathId)}
            className={styles.slideWrapper}
            key={provider.id}
          >
            <ProviderSummary
              className={styles.slide}
              isPromotionLabelHidden
              // NOTE: To prevent image flickering
              isDisableImgLazyload
              {...getProviderProps(provider)}
            />
          </Link>
        ))}
      </Swiper>
    </div>
  );
}

export default ProviderSummarySlider;
